import { FC, useMemo, useState } from 'react';

import { Col, Flex, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import IconError from '@/assets/icons/icon-error.svg?react';
import IconInfo from '@/assets/icons/icon-info.svg?react';
import Card from '@/components/RCard';
import RToggleButtons from '@/components/RToggleButtons';
import Tooltip from '@/components/RTooltip';
import useCurrency from '@/contexts/useCurrency';
import { DataProps } from '@/types/result';
import { formatCompactNumber } from '@/utils';

import StyledTable from './styles';

const KeyMetricsTable: FC<DataProps> = ({
  data,
  isLoading,
  error,
  activeTab,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { currencySymbol } = useCurrency();
  const buttons = [
    t('result.summary.keyMetrics.buttons.beforeStorage'),
    t('result.summary.keyMetrics.buttons.afterStorage'),
    t('result.summary.keyMetrics.buttons.storage'),
  ];
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const currentTab = typeof activeTab === 'number' ? activeTab : activeIndex;
  const handleButtonClick = (index: number) => {
    setActiveIndex(index);
  };

  const metrics = useMemo(() => {
    if (!data) return null;

    const { result } = data;
    const metricsData = [
      {
        capex: `${currencySymbol}${formatCompactNumber(result.capexWithoutStorage || 0)}`,
        earnings: `${currencySymbol}${formatCompactNumber(result.revenueWos || 0)}`,
        editdaMargin: `% ${result.ebitdaMarginWos.toFixed(2) || 0}`,
        grossProfit: `${currencySymbol}${formatCompactNumber(result.grossProfitWos || 0)}`,
        irr: `% ${result.projectIrrWos.toFixed(2) || 0}`,
        equityIrr: `% ${result.equityIrrWos.toFixed(2) || 0}`,
        payback:
          result.discountedPaybackPeriodProjectWos === 0
            ? '0'
            : `${result.discountedPaybackPeriodProjectWos.toFixed(0)} Years`,
      },
      {
        capex: `${currencySymbol}${formatCompactNumber(result.capexWithStorage || 0)}`,
        earnings: `${currencySymbol}${formatCompactNumber(result.totalRevenue || 0)}`,
        editdaMargin: `% ${result.ebitdaMargin.toFixed(2) || 0}`,
        grossProfit: `${currencySymbol}${formatCompactNumber(result.grossProfit || 0)}`,
        irr: `% ${result.projectIrr.toFixed(2) || 0}`,
        equityIrr: `% ${result.equityIrr.toFixed(2) || 0}`,
        payback:
          result.discountedPaybackPeriodProject === 0
            ? '0'
            : `${result.discountedPaybackPeriodProject.toFixed(0)} Years`,
      },
      {
        capex: `${currencySymbol}${formatCompactNumber(result.capexStorageOnly || 0)}`,
        earnings: `${currencySymbol}${formatCompactNumber(result.revenueFromStorage || 0)}`,
        editdaMargin: `% ${result.ebitdaMarginStorage.toFixed(2) || 0}`,
        grossProfit: `${currencySymbol}${formatCompactNumber(result.grossProfitStorage || 0)}`,
        irr: `% ${result.projectIrrStorage.toFixed(2) || 0}`,
        equityIrr: `% ${result.equityIrrStorage.toFixed(2) || 0}`,
        payback:
          result.discountedPaybackPeriodProjectStorage === 0
            ? '0'
            : `${result.discountedPaybackPeriodProjectStorage.toFixed(0)} Years`,
      },
    ];

    return metricsData[currentTab];
  }, [data, currentTab, currencySymbol]);
  return (
    <Card
      title={
        <Flex gap={8} align="center">
          <Typography.Title level={4} className="fs-17-bold">
            {t('result.summary.keyMetrics.title')}
          </Typography.Title>
          <Tooltip
            title={t('result.summary.keyMetrics.tooltip')}
            description={t('result.summary.keyMetrics.description')}
          >
            <IconInfo />
          </Tooltip>
        </Flex>
      }
      extra={
        <RToggleButtons
          labels={buttons}
          onButtonClick={handleButtonClick}
          initialActiveIndex={activeTab}
        />
      }
      $paddingBody="12px 0"
      styles={{ header: { padding: '14px 24px' } }}
      style={{ marginTop: '24px' }}
    >
      {isLoading ? (
        <Flex justify="center" align="center" style={{ height: 81 }}>
          <Spin style={{ margin: '20px' }} />
        </Flex>
      ) : error ? (
        <Flex
          vertical
          gap={12}
          justify="center"
          align="center"
          style={{ height: 81 }}
        >
          <IconError width={50} height={50} fill={theme.colors.tagFailure} />
          <Typography.Text className="fs-14-regular text-gray-color">
            {t('result.error.fetchDataError')}
          </Typography.Text>
        </Flex>
      ) : (
        <StyledTable vertical gap={8} justify="center" align="center">
          <Flex gap={8} justify="center" align="center">
            <Typography.Text className="title">
              {buttons[currentTab]}
            </Typography.Text>
          </Flex>
          <Flex justify="space-around" className="w-100">
            <Col className="column">
              <Typography.Text className="value">
                {metrics?.capex}
              </Typography.Text>
              <Typography.Text className="name">
                {t('result.summary.keyMetrics.columns.investmentCapex')}
              </Typography.Text>
            </Col>

            <Col className="column">
              <Typography.Text className="value">
                {metrics?.earnings}
              </Typography.Text>
              <Typography.Text className="name">
                {t('result.summary.keyMetrics.columns.totalEarnings')}
              </Typography.Text>
            </Col>

            <Col className="column">
              <Typography.Text className="value">
                {metrics?.editdaMargin}
              </Typography.Text>
              <Typography.Text className="name">
                {t('result.summary.keyMetrics.columns.ebitdaMargin')}
              </Typography.Text>
            </Col>

            <Col className="column">
              <Typography.Text className="value">
                {metrics?.grossProfit}
              </Typography.Text>
              <Typography.Text className="name">
                {t('result.summary.keyMetrics.columns.grossProfit')}
              </Typography.Text>
            </Col>

            <Col className="column">
              <Typography.Text className="value">
                {metrics?.irr}
              </Typography.Text>
              <Typography.Text className="name">
                {t('result.summary.keyMetrics.columns.irr')}
              </Typography.Text>
            </Col>

            <Col className="column">
              <Typography.Text className="value">
                {metrics?.equityIrr}
              </Typography.Text>
              <Typography.Text className="name">
                {t('result.summary.keyMetrics.columns.equityIrr')}
              </Typography.Text>
            </Col>

            <Col className="column">
              <Typography.Text className="value">
                {metrics?.payback}
              </Typography.Text>
              <Typography.Text className="name">
                {t('result.summary.keyMetrics.columns.discountedPaybackPeriod')}
              </Typography.Text>
            </Col>
          </Flex>
        </StyledTable>
      )}
    </Card>
  );
};
export default KeyMetricsTable;
